import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { loadPointValues } from "./pointValues";

function flatten(obj, prefix = "", out = {}) {
  if (obj === null || obj === undefined) {
    out[prefix] = obj;
    return out;
  }

  if (Array.isArray(obj)) {
    if (obj.length === 0) {
      out[prefix] = "[]";
    } else {
      obj.forEach((item, i) => {
        flatten(item, `${prefix}[${i}]`, out);
      });
    }
    return out;
  }

  if (typeof obj === "object") {
    Object.keys(obj).forEach((key) => {
      const newPrefix = prefix ? `${prefix}.${key}` : key;

      flatten(obj[key], newPrefix, out);
    });

    return out;
  }

  out[prefix] = obj;
  return out;
}

// ------------------------------------------------------------
// Faculty name
// ------------------------------------------------------------
function getFacultyName(facultyData) {
  const info = facultyData?.personal_information?.[0];

  if (!info) return "N/A";

  const salutation = info.salutation?.trim() || "";
  const name = info.name?.trim() || "";

  if (salutation || name) {
    return `${salutation} ${name}`.trim();
  }

  return "(Profile not completed)";
}

// ------------------------------------------------------------
// Doctoral / Masters detection
// ------------------------------------------------------------
function detectDoctoralInfo(facultyData) {
  const educationRows = facultyData?.education || [];

  let hasDoctoral = false;
  let hasMasters = false;
  let doctoralDate = null;

  educationRows.forEach((row) => {
    const rowText = Object.values(row)
      .filter((v) => typeof v === "string")
      .join(" ")
      .toLowerCase();

    const isDoctoralRow = rowText.includes("doctoral");
    const isMastersRow = rowText.includes("masters");

    if (isDoctoralRow) {
      hasDoctoral = true;

      Object.entries(row).forEach(([col, val]) => {
        if (val == null) return;

        const colLower = col.toLowerCase();

        const looksLikeDate =
          colLower.includes("year") || colLower.includes("date");

        if (!looksLikeDate) return;

        const parsed = new Date(val);

        if (isNaN(parsed.getTime())) return;

        if (!doctoralDate || parsed > doctoralDate) {
          doctoralDate = parsed;
        }
      });
    }

    if (isMastersRow) {
      hasMasters = true;
    }
  });

  return {
    hasDoctoral,
    hasMasters,
    doctoralDate,
  };
}

const SIX_YEARS_MS = 6 * 365.25 * 24 * 60 * 60 * 1000;

function isDoctoralWithinLastSixYears(doctoralDate) {
  if (!doctoralDate) return false;

  const now = new Date();

  return now.getTime() - doctoralDate.getTime() <= SIX_YEARS_MS;
}

// ------------------------------------------------------------
// Group A.1.1
// ------------------------------------------------------------
const A111_KEYS = [
  "journals.q1_top",
  "journals.q1",
  "journals.q2",
  "journals.q3",
  "journals.q4",
];

const A111_SA_THRESHOLD = 45;
const A111_SP_THRESHOLD = 20;

// ------------------------------------------------------------
// Group A.1.2 to B
// ------------------------------------------------------------
const A112_TO_B_KEYS = [
  "books.book_intl",
  "books.book_local",

  "chapters.chapter_bd",
  "chapters.chapter_intl",

  "book_review.book_review_intl",
  "book_review.book_review_local",

  "conferences.conf_proc_intl",
  "conferences.conf_proc_bd",
  "conferences.conf_pres_intl",
  "conferences.conf_pres_bd",

  "trade.trade_intl",
  "trade.trade_bd",

  "others.industry_reports",
  "others.opeds",

  "textbooks.textbook_new",
  "textbooks.textbook_revision",

  "cases.case_indexed",
  "cases.case_nonindexed_used",
  "cases.case_nonindexed",

  "grants.grant_15m",
  "grants.grant_08_149",
  "grants.grant_03_079",
  "grants.grant_01_029",

  "editorial_editor.q1_top",
  "editorial_editor.q1",
  "editorial_editor.q2",
  "editorial_editor.q3",
  "editorial_editor.q4",

  "editorial_special.q1_top",
  "editorial_special.q1",
  "editorial_special.q2",
  "editorial_special.q3",
  "editorial_special.q4",

  "editorial_board.q1_top",
  "editorial_board.q1",
  "editorial_board.q2",
  "editorial_board.q3",
  "editorial_board.q4",

  "editorial_reviewer.q1_top",
  "editorial_reviewer.q1",
  "editorial_reviewer.q2",
  "editorial_reviewer.q3",
  "editorial_reviewer.q4",

  "conference_review.intl",
  "conference_review.local",

  "trade_editorial.intl",
  "trade_editorial.local",

  "trade_board.intl",
  "trade_board.local",

  "trade_review.intl",
  "trade_review.local",

  "grant_evaluation.count",

  "supervision_phd.chair",
  "supervision_phd.member",

  "supervision_masters.chair",
  "supervision_masters.member",

  "proposal_writing_external.external",
  "proposal_writing_internal.internal",

  "conference_org.chair",
  "conference_org.cochair",
  "conference_org.member",

  "keynote.intl",
  "keynote.local",

  "program_dev.count",
  "program_dev.member",

  "course_dev.lead_new",
  "course_dev.member_new",
  "course_dev.course_coordination",

  "visiting_scholar.count",

  "academic_admin_role_years.academic_admin_role_years",
];

const A111_PLUS_A112_TO_B_THRESHOLD = 45;

// ------------------------------------------------------------
// Group C
// ------------------------------------------------------------
const C_KEYS = [
  "consultation_hours.consultation_hours",

  "industry_fulltime_years.industry_fulltime_years",
  "industry_parttime_years.industry_parttime_years",

  "business_ownership_years.business_ownership_years",

  "board_chairman_count.board_chairman_count",
  "board_member_count.board_member_count",

  "speaker_intl_count.speaker_intl_count",
  "speaker_local_count.speaker_local_count",

  "training_sessions_count.training_sessions_count",

  "media_contributions_count.media_contributions_count",

  "prof_cert_intl_count.prof_cert_intl_count",
  "prof_cert_local_count.prof_cert_local_count",

  "prof_cert_total_years.prof_cert_total_years",

  "new_exec_courses_count.count",

  "policy_advisory_roles_count.policy_advisory_roles_count",

  "policy_briefs_intl.policy_briefs_intl",
  "policy_briefs_local.policy_briefs_local",

  "ip_commercialization_count.ip_commercialization_count",

  "pro_bono_count.pro_bono_count",

  "collaborative_count.collaborative_count",
];

const C_THRESHOLD = 45;

const GRAND_TOTAL_THRESHOLD = 60;

// ============================================================
// PUBLICATION TABLE 1 GROUPS
// ============================================================

// A — Basic or Discovery, first column
const PUBLICATION_A_COLUMN_1_KEYS = [
  "journals.q1_top",
  "journals.q1",
  "journals.q2",
  "journals.q3",
  "journals.q4",

  "books.book_intl",
  "books.book_local",

  "chapters.chapter_intl",
  "chapters.chapter_bd",
];

// A — Basic or Discovery, second column
const PUBLICATION_A_COLUMN_2_KEYS = [
  "conferences.conf_proc_intl",
  "conferences.conf_proc_bd",
  "conferences.conf_pres_intl",
  "conferences.conf_pres_bd",
];

// B — Applied or Integrative, first column
const PUBLICATION_B_COLUMN_1_KEYS = [
  "trade.trade_intl",
  "trade.trade_bd",
  "others.industry_reports",
];

// B — Applied or Integrative, second column
const PUBLICATION_B_COLUMN_2_KEYS = [];

// C — Teaching and Learning, first column (Book Reviews)
const PUBLICATION_C_COLUMN_1_KEYS = [
  "book_review.book_review_intl",
  "book_review.book_review_local",
];

// C — Teaching and Learning, second column
const PUBLICATION_C_COLUMN_2_KEYS = [
  "others.opeds",

  "textbooks.textbook_new",
  "textbooks.textbook_revision",

  "cases.case_indexed",
  "cases.case_nonindexed_used",
  "cases.case_nonindexed",
];

// ------------------------------------------------------------
// Get numeric raw value
// ------------------------------------------------------------
function getRawNumericValue(flatMetrics, key) {
  const value = flatMetrics[key];

  const numericValue = typeof value === "number" ? value : Number(value);

  if (isNaN(numericValue)) {
    return 0;
  }

  return numericValue;
}

// ------------------------------------------------------------
// Sum a group of raw variables
// ------------------------------------------------------------
function sumPublicationKeys(flatMetrics, keys) {
  return keys.reduce((sum, key) => {
    return sum + getRawNumericValue(flatMetrics, key);
  }, 0);
}

// ============================================================
// PUBLICATION TABLE 2 GROUPS (per table_2.xlsx)
// ============================================================

// X — PRJ Articles
// Column 1: PRJ Articles (X)
const TABLE2_X_COLUMN_1_KEYS = [
  "journals.q1_top",
  "journals.q1",
  "journals.q2",
  "journals.q3",
  "journals.q4",
];

// Column 2: Trade Journals (X)
const TABLE2_X_COLUMN_2_KEYS = ["trade.trade_intl", "trade.trade_bd"];

// Column 3: Cases (X)
const TABLE2_X_COLUMN_3_KEYS = [
  "cases.case_indexed",
  "cases.case_nonindexed_used",
  "cases.case_nonindexed",
];

// Y — Other Peer-reviewed ICs
// Column 1: Books & BCs (Y)
const TABLE2_Y_COLUMN_1_KEYS = [
  "books.book_intl",
  "books.book_local",
  "chapters.chapter_intl",
  "chapters.chapter_bd",
];

// Column 2: Conference Proceedings & Presentations (Y)
const TABLE2_Y_COLUMN_2_KEYS = [
  "conferences.conf_proc_intl",
  "conferences.conf_proc_bd",
  "conferences.conf_pres_intl",
  "conferences.conf_pres_bd",
];

// Column 3: always 0
const TABLE2_Y_COLUMN_3_KEYS = [];

// Z — All Other ICs
// Column 1: Book Reviews (Z)
const TABLE2_Z_COLUMN_1_KEYS = [
  "book_review.book_review_intl",
  "book_review.book_review_local",
];

// Column 2: Industry Reports, Textbooks & Op-eds (Z)
const TABLE2_Z_COLUMN_2_KEYS = [
  "others.industry_reports",
  "others.opeds",
  "textbooks.textbook_new",
  "textbooks.textbook_revision",
];

// Column 3: always 0
const TABLE2_Z_COLUMN_3_KEYS = [];

// ============================================================
// COMPONENT
// ============================================================

export default function AacsbRawMetricsPage() {
  const navigate = useNavigate();
  const { facultyId } = useParams();

  const [flatMetrics, setFlatMetrics] = useState(null);

  const [pointMap, setPointMap] = useState(null);

  const [doctoralInfo, setDoctoralInfo] = useState(null);

  const [facultyName, setFacultyName] = useState("");

  const [error, setError] = useState(null);

  // ==========================================================
  // LOAD DATA
  // ==========================================================

  useEffect(() => {
    // Load AACSB metrics
    try {
      const raw = localStorage.getItem("aacsbMetrics");

      if (!raw) {
        setError(
          "No 'aacsbMetrics' key found in localStorage. Open the AACSB Dashboard page first — it computes and saves this value."
        );

        return;
      }

      const stored = JSON.parse(raw);

      setFlatMetrics(flatten(stored));
    } catch (err) {
      console.error("Failed to parse aacsbMetrics:", err);

      setError(
        "Found 'aacsbMetrics' in localStorage but couldn't parse it as JSON. See console."
      );

      return;
    }

    // Load faculty data
    try {
      const rawFaculty = localStorage.getItem("facultyData");

      if (rawFaculty) {
        const facultyData = JSON.parse(rawFaculty);

        setDoctoralInfo(detectDoctoralInfo(facultyData));

        setFacultyName(getFacultyName(facultyData));
      }
    } catch (err) {
      console.error("Failed to parse facultyData:", err);
    }

    // Load point values
    loadPointValues()
      .then((map) => {
        if (Object.keys(map).length === 0) {
          setError(
            "point_values.csv loaded but produced zero valid rows. Check the file is at public/data/point_values.csv and its format."
          );
        }

        setPointMap(map);
      })
      .catch((err) => {
        console.error("Failed to fetch point_values.csv:", err);

        setError(
          "Could not fetch /data/point_values.csv. See console/network tab."
        );
      });
  }, []);

  // ==========================================================
  // ERROR
  // ==========================================================

  if (error) {
    return (
      <div style={{ padding: 20 }}>
        <button
          onClick={() => navigate(-1)}
          style={{
            marginBottom: 20,
          }}
        >
          ← Back
        </button>

        <p
          style={{
            color: "#b91c1c",
            fontWeight: "bold",
          }}
        >
          {error}
        </p>
      </div>
    );
  }

  // ==========================================================
  // LOADING
  // ==========================================================

  if (!flatMetrics || !pointMap) {
    return (
      <div style={{ padding: 20 }}>
        <p>
          Loading… (flatMetrics: {flatMetrics ? "✅" : "⏳"}, pointMap:{" "}
          {pointMap ? "✅" : "⏳"})
        </p>
      </div>
    );
  }

  // ==========================================================
  // SCORED ROWS
  // ==========================================================

  const scoredRows = Object.entries(flatMetrics)
    .filter(([key]) => pointMap[key] !== undefined)
    .map(([key, value]) => {
      const numericValue = typeof value === "number" ? value : Number(value);

      const { id, weight } = pointMap[key];

      let score;

      if (key === "consultation_hours.consultation_hours") {
        score = isNaN(numericValue) ? null : (numericValue / 100) * 15;
      } else {
        score = isNaN(numericValue) ? null : numericValue * weight;
      }

      return {
        key,
        value,
        id,
        weight,
        score,
      };
    });

  // ==========================================================
  // GRAND TOTAL
  // ==========================================================

  const grandTotal = scoredRows.reduce((sum, row) => sum + (row.score ?? 0), 0);

  // ==========================================================
  // GROUP TOTAL HELPER
  // ==========================================================

  const sumGroup = (keys) =>
    keys.reduce((sum, key) => {
      const row = scoredRows.find((r) => r.key === key);

      return sum + (row?.score ?? 0);
    }, 0);

  const a111Total = sumGroup(A111_KEYS);

  const a112ToBTotal = sumGroup(A112_TO_B_KEYS);

  const cTotal = sumGroup(C_KEYS);

  // ==========================================================
  // DEGREE STATUS
  // ==========================================================

  const hasDoctoral = doctoralInfo?.hasDoctoral ?? false;

  const hasMasters = doctoralInfo?.hasMasters ?? false;

  const recentDoctoral = doctoralInfo
    ? isDoctoralWithinLastSixYears(doctoralInfo.doctoralDate)
    : false;

  // ==========================================================
  // SA
  // ==========================================================

  let saRank;
  let saRankPath;

  if (hasDoctoral && recentDoctoral) {
    saRank = "SA";

    saRankPath = "PhD within last 6 years — automatic qualification";
  } else if (hasDoctoral && !recentDoctoral) {
    const meetsThresholds =
      grandTotal >= GRAND_TOTAL_THRESHOLD && a111Total >= A111_SA_THRESHOLD;

    saRank = meetsThresholds ? "SA" : "Not Applicable";

    saRankPath = `PhD older than 6 years — checked against Grand Total >= ${GRAND_TOTAL_THRESHOLD} and SA Points >= ${A111_SA_THRESHOLD}`;
  } else {
    saRank = "Not Applicable";

    saRankPath = "No doctoral degree on record — SA does not apply";
  }

  // ==========================================================
  // PA
  // ==========================================================

  let paRank;
  let paRankPath;

  if (hasDoctoral) {
    const meetsThresholds =
      grandTotal >= GRAND_TOTAL_THRESHOLD && cTotal >= C_THRESHOLD;

    paRank = meetsThresholds ? "PA" : "Not Applicable";

    paRankPath = `Has doctoral degree — checked against Grand Total >= ${GRAND_TOTAL_THRESHOLD} and PA Points >= ${C_THRESHOLD}`;
  } else {
    paRank = "Not Applicable";

    paRankPath = "No doctoral degree on record — PA does not apply";
  }

  // ==========================================================
  // SP
  // ==========================================================

  const a111PlusA112ToB = a111Total + a112ToBTotal;

  let spRank;
  let spRankPath;

  if (!hasDoctoral && hasMasters) {
    const meetsThresholds =
      grandTotal >= GRAND_TOTAL_THRESHOLD &&
      a111Total >= A111_SP_THRESHOLD &&
      a111PlusA112ToB >= A111_PLUS_A112_TO_B_THRESHOLD;

    spRank = meetsThresholds ? "SP" : "Not Applicable";

    spRankPath = `No PhD, has Masters — checked against Grand Total >= ${GRAND_TOTAL_THRESHOLD}, A.1.1 alone >= ${A111_SP_THRESHOLD}, and A.1.1 + A.1.2-to-B combined >= ${A111_PLUS_A112_TO_B_THRESHOLD}`;
  } else if (hasDoctoral) {
    spRank = "Not Applicable";

    spRankPath = "Has a doctoral degree — SP does not apply";
  } else {
    spRank = "Not Applicable";

    spRankPath = "No masters degree on record";
  }

  // ==========================================================
  // IP
  // ==========================================================

  let ipRank;
  let ipRankPath;

  if (!hasDoctoral && hasMasters) {
    const meetsThresholds =
      cTotal >= C_THRESHOLD && grandTotal >= GRAND_TOTAL_THRESHOLD;

    ipRank = meetsThresholds ? "IP" : "Not Applicable";

    ipRankPath = `No PhD, has Masters — checked against IP Points (C) >= ${C_THRESHOLD} and Grand Total >= ${GRAND_TOTAL_THRESHOLD}`;
  } else if (hasDoctoral) {
    ipRank = "Not Applicable";

    ipRankPath = "Has a doctoral degree — IP does not apply";
  } else {
    ipRank = "Not Applicable";

    ipRankPath = "No masters degree on record";
  }

  // ==========================================================
  // A
  // ==========================================================

  let aRank;
  let aRankPath;

  if (hasDoctoral) {
    if (saRank !== "SA" && paRank !== "PA") {
      aRank = "A";

      aRankPath = "Has doctoral degree but did not qualify for SA or PA";
    } else {
      aRank = "Not Applicable";

      aRankPath = "Has doctoral degree and already qualified for SA or PA";
    }
  } else if (hasMasters) {
    if (spRank !== "SP" && ipRank !== "IP") {
      aRank = "A";

      aRankPath =
        "Has masters degree (no doctoral) but did not qualify for SP or IP";
    } else {
      aRank = "Not Applicable";

      aRankPath = "Has masters degree and already qualified for SP or IP";
    }
  } else {
    aRank = "Not Applicable";

    aRankPath = "No doctoral or masters degree on record";
  }

  // ==========================================================
  // PUBLICATION TABLE 1
  // ==========================================================

  const publicationAColumn1 = sumPublicationKeys(
    flatMetrics,
    PUBLICATION_A_COLUMN_1_KEYS
  );

  const publicationAColumn2 = sumPublicationKeys(
    flatMetrics,
    PUBLICATION_A_COLUMN_2_KEYS
  );

  const publicationATotal = publicationAColumn1 + publicationAColumn2;

  const publicationBColumn1 = sumPublicationKeys(
    flatMetrics,
    PUBLICATION_B_COLUMN_1_KEYS
  );

  const publicationBColumn2 = sumPublicationKeys(
    flatMetrics,
    PUBLICATION_B_COLUMN_2_KEYS
  );

  const publicationBTotal = publicationBColumn1 + publicationBColumn2;

  const publicationCColumn1 = sumPublicationKeys(
    flatMetrics,
    PUBLICATION_C_COLUMN_1_KEYS
  );

  const publicationCColumn2 = sumPublicationKeys(
    flatMetrics,
    PUBLICATION_C_COLUMN_2_KEYS
  );

  const publicationCTotal = publicationCColumn1 + publicationCColumn2;

  const publicationColumn1Total =
    publicationAColumn1 + publicationBColumn1 + publicationCColumn1;

  const publicationColumn2Total =
    publicationAColumn2 + publicationBColumn2 + publicationCColumn2;

  const publicationGrandTotal =
    publicationColumn1Total + publicationColumn2Total;

  // ==========================================================
  // PUBLICATION TABLE 2
  // ==========================================================

  // X — PRJ Articles
  const table2XColumn1 = sumPublicationKeys(
    flatMetrics,
    TABLE2_X_COLUMN_1_KEYS
  );

  const table2XColumn2 = sumPublicationKeys(
    flatMetrics,
    TABLE2_X_COLUMN_2_KEYS
  );

  const table2XColumn3 = sumPublicationKeys(
    flatMetrics,
    TABLE2_X_COLUMN_3_KEYS
  );

  const table2XTotal = table2XColumn1 + table2XColumn2 + table2XColumn3;

  // Y — Other Peer-reviewed ICs
  const table2YColumn1 = sumPublicationKeys(
    flatMetrics,
    TABLE2_Y_COLUMN_1_KEYS
  );

  const table2YColumn2 = sumPublicationKeys(
    flatMetrics,
    TABLE2_Y_COLUMN_2_KEYS
  );

  const table2YColumn3 = sumPublicationKeys(
    flatMetrics,
    TABLE2_Y_COLUMN_3_KEYS
  );

  const table2YTotal = table2YColumn1 + table2YColumn2 + table2YColumn3;

  // Z — All Other ICs
  const table2ZColumn1 = sumPublicationKeys(
    flatMetrics,
    TABLE2_Z_COLUMN_1_KEYS
  );

  const table2ZColumn2 = sumPublicationKeys(
    flatMetrics,
    TABLE2_Z_COLUMN_2_KEYS
  );

  const table2ZColumn3 = sumPublicationKeys(
    flatMetrics,
    TABLE2_Z_COLUMN_3_KEYS
  );

  const table2ZTotal = table2ZColumn1 + table2ZColumn2 + table2ZColumn3;

  // Bottom totals
  const table2Column1Total = table2XColumn1 + table2YColumn1 + table2ZColumn1;

  const table2Column2Total = table2XColumn2 + table2YColumn2 + table2ZColumn2;

  const table2Column3Total = table2XColumn3 + table2YColumn3 + table2ZColumn3;

  const table2GrandTotal = table2XTotal + table2YTotal + table2ZTotal;

  // ==========================================================
  // STYLES
  // ==========================================================

  const cardStyle = {
    border: "2px solid #1d4786",
    borderRadius: 8,
    padding: 16,
  };

  const labelStyle = {
    fontSize: "0.85rem",
    color: "#555",
  };

  const valueStyle = {
    fontSize: "1.5rem",
    fontWeight: "bold",
  };

  const sectionTitleStyle = {
    fontSize: "1.1rem",
    fontWeight: "bold",
    margin: "24px 0 8px",
    color: "#1d4786",
  };

  const saPassed = saRank !== "Not Applicable";

  const paPassed = paRank !== "Not Applicable";

  const spPassed = spRank !== "Not Applicable";

  const ipPassed = ipRank !== "Not Applicable";

  const aPassed = aRank !== "Not Applicable";

  // ==========================================================
  // TABLE STYLES
  // ==========================================================

  const tableHeaderStyle = {
    border: "1px solid #000",
    padding: "10px 8px",
    textAlign: "center",
    backgroundColor: "#dbeafe",
    fontWeight: "bold",
  };

  const tableCellStyle = {
    border: "1px solid #000",
    padding: "10px 8px",
  };

  const numberCellStyle = {
    ...tableCellStyle,
    textAlign: "center",
    fontWeight: "bold",
  };

  const gridStyle = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: 16,
  };

  const degreeCardStyle = (active) => ({
    ...cardStyle,
    borderColor: active ? "#16a34a" : "#999",
    backgroundColor: active ? "#f0fdf4" : "transparent",
    padding: "10px 16px",
  });

  const degreeValueStyle = (active) => ({
    fontWeight: "bold",
    color: active ? "#16a34a" : "#999",
  });

  const rankCardStyle = (passed) => ({
    ...cardStyle,
    borderColor: passed ? "#16a34a" : "#999",
    backgroundColor: passed ? "#f0fdf4" : "transparent",
  });

  const rankValueStyle = (passed) => ({
    ...valueStyle,
    color: passed ? "#16a34a" : "#999",
  });

  const varCellStyle = {
    border: "1px solid #000",
    padding: 8,
  };

  const varHeaderStyle = {
    border: "1px solid #000",
    padding: 8,
    textAlign: "left",
  };

  // ==========================================================
  // RETURN
  // ==========================================================

  return (
    <div
      style={{
        padding: 20,
        fontFamily: "monospace",
      }}
    >
      {/* ====================================================
          BACK
      ==================================================== */}

      <button
        onClick={() => navigate(-1)}
        style={{
          padding: "8px 16px",
          fontSize: "1rem",
          backgroundColor: "#1d4786",
          color: "#fff",
          border: "none",
          borderRadius: "5px",
          cursor: "pointer",
          marginBottom: 20,
        }}
      >
        ← Back
      </button>

      <h2>AACSB Variables × Point Weight</h2>

      {facultyName && (
        <h3
          style={{
            marginTop: "5px",
            fontSize: "1.2rem",
            background: "#1d4786a1",
            color: "#fff",
            padding: "6px 10px",
            borderRadius: "5px",
            display: "inline-block",
          }}
        >
          {facultyName} (PIN: {facultyId})
        </h3>
      )}

      {/* ====================================================
          DEGREE STATUS
      ==================================================== */}

      {doctoralInfo && (
        <div
          style={{
            display: "flex",
            gap: 16,
            margin: "16px 0",
            flexWrap: "wrap",
          }}
        >
          <div style={degreeCardStyle(hasDoctoral)}>
            <span style={labelStyle}>Doctoral Degree: </span>

            <span style={degreeValueStyle(hasDoctoral)}>
              {hasDoctoral ? "Yes" : "No"}
            </span>
          </div>

          <div style={degreeCardStyle(recentDoctoral)}>
            <span style={labelStyle}>PhD &le; 6 years: </span>

            <span style={degreeValueStyle(recentDoctoral)}>
              {recentDoctoral ? "Yes" : "No"}
            </span>

            {doctoralInfo.doctoralDate && (
              <span
                style={{
                  ...labelStyle,
                  marginLeft: 8,
                }}
              >
                (detected:{" "}
                {doctoralInfo.doctoralDate.toISOString().slice(0, 10)})
              </span>
            )}
          </div>

          <div style={degreeCardStyle(hasMasters)}>
            <span style={labelStyle}>Master's Degree: </span>

            <span style={degreeValueStyle(hasMasters)}>
              {hasMasters ? "Yes" : "No"}
            </span>
          </div>
        </div>
      )}

      {/* ====================================================
          SA
      ==================================================== */}

      <div style={sectionTitleStyle}>SA</div>

      <div style={gridStyle}>
        <div style={cardStyle}>
          <div style={labelStyle}>Grand Total</div>

          <div style={valueStyle}>{grandTotal.toFixed(2)}</div>
        </div>

        <div style={cardStyle}>
          <div style={labelStyle}>SA Points (A.1.1)</div>

          <div style={valueStyle}>{a111Total.toFixed(2)}</div>
        </div>

        <div style={rankCardStyle(saPassed)}>
          <div style={labelStyle}>Rank — {saRankPath}</div>

          <div style={rankValueStyle(saPassed)}>{saRank}</div>
        </div>
      </div>

      {/* ====================================================
          PA
      ==================================================== */}

      <div style={sectionTitleStyle}>PA</div>

      <div style={gridStyle}>
        <div style={cardStyle}>
          <div style={labelStyle}>Grand Total</div>

          <div style={valueStyle}>{grandTotal.toFixed(2)}</div>
        </div>

        <div
          style={{
            ...cardStyle,
            borderColor: "#b45309",
          }}
        >
          <div style={labelStyle}>PA Points (C)</div>

          <div style={valueStyle}>{cTotal.toFixed(2)}</div>
        </div>

        <div style={rankCardStyle(paPassed)}>
          <div style={labelStyle}>Rank — {paRankPath}</div>

          <div style={rankValueStyle(paPassed)}>{paRank}</div>
        </div>
      </div>

      {/* ====================================================
          SP
      ==================================================== */}

      <div style={sectionTitleStyle}>SP</div>

      <div style={gridStyle}>
        <div style={cardStyle}>
          <div style={labelStyle}>Grand Total</div>

          <div style={valueStyle}>{grandTotal.toFixed(2)}</div>
        </div>

        <div
          style={{
            ...cardStyle,
            borderColor: "#7c3aed",
          }}
        >
          <div style={labelStyle}>SP Points (A.1.1 alone)</div>

          <div style={valueStyle}>{a111Total.toFixed(2)}</div>
        </div>

        <div
          style={{
            ...cardStyle,
            borderColor: "#7c3aed",
          }}
        >
          <div style={labelStyle}>SP Points (A.1.2 to B alone)</div>

          <div style={valueStyle}>{a112ToBTotal.toFixed(2)}</div>
        </div>

        <div
          style={{
            ...cardStyle,
            borderColor: "#7c3aed",
          }}
        >
          <div style={labelStyle}>
            SP Points (A.1.1 + A.1.2-to-B combined)
          </div>

          <div style={valueStyle}>{a111PlusA112ToB.toFixed(2)}</div>
        </div>

        <div style={rankCardStyle(spPassed)}>
          <div style={labelStyle}>Rank — {spRankPath}</div>

          <div style={rankValueStyle(spPassed)}>{spRank}</div>
        </div>
      </div>

      {/* ====================================================
          IP
      ==================================================== */}

      <div style={sectionTitleStyle}>IP</div>

      <div style={gridStyle}>
        <div style={cardStyle}>
          <div style={labelStyle}>Grand Total</div>

          <div style={valueStyle}>{grandTotal.toFixed(2)}</div>
        </div>

        <div
          style={{
            ...cardStyle,
            borderColor: "#0891b2",
          }}
        >
          <div style={labelStyle}>IP Points (C)</div>

          <div style={valueStyle}>{cTotal.toFixed(2)}</div>
        </div>

        <div style={rankCardStyle(ipPassed)}>
          <div style={labelStyle}>Rank — {ipRankPath}</div>

          <div style={rankValueStyle(ipPassed)}>{ipRank}</div>
        </div>
      </div>

      {/* ====================================================
          A
      ==================================================== */}

      <div style={sectionTitleStyle}>A</div>

      <div style={gridStyle}>
        <div style={cardStyle}>
          <div style={labelStyle}>Grand Total</div>

          <div style={valueStyle}>{grandTotal.toFixed(2)}</div>
        </div>

        <div style={rankCardStyle(aPassed)}>
          <div style={labelStyle}>Rank — {aRankPath}</div>

          <div style={rankValueStyle(aPassed)}>{aRank}</div>
        </div>
      </div>

      {/* ====================================================
          PUBLICATION TABLE — TABLE 1
      ==================================================== */}

      <div style={sectionTitleStyle}>Publication Type / Publication Count</div>

      <div
        style={{
          width: "100%",
          overflowX: "auto",
          marginTop: 12,
        }}
      >
        <table
          style={{
            width: "100%",
            minWidth: 900,
            borderCollapse: "collapse",
            backgroundColor: "#fff",
          }}
        >
          <thead>
            <tr>
              <th
                rowSpan={2}
                style={{
                  ...tableHeaderStyle,
                  width: "20%",
                }}
              >
                Publication Type
              </th>

              <th colSpan={2} style={tableHeaderStyle}>
                Publication Count
              </th>

              <th
                rowSpan={2}
                style={{
                  ...tableHeaderStyle,
                  width: "15%",
                }}
              >
                Total
              </th>
            </tr>

            <tr>
              <th
                style={{
                  ...tableHeaderStyle,
                  width: "32.5%",
                }}
              >
                PRJs, Books, BCs (A) /
                <br />
                Trade Journals &amp; Industry Reports (B) /
                <br />
                Book Reviews (C)
              </th>

              <th
                style={{
                  ...tableHeaderStyle,
                  width: "32.5%",
                }}
              >
                Conf. Proceedings &amp; Presentations (A) /
                <br />
                Op-eds, Textbooks &amp; Cases (C)
              </th>
            </tr>
          </thead>

          <tbody>
            <tr>
              <td
                style={{
                  ...tableCellStyle,
                  fontWeight: "bold",
                  backgroundColor: "#eff6ff",
                }}
              >
                A (Basic or Discovery)
              </td>

              <td
                style={{
                  ...numberCellStyle,
                  backgroundColor: "#eff6ff",
                }}
              >
                {publicationAColumn1}
              </td>

              <td
                style={{
                  ...numberCellStyle,
                  backgroundColor: "#eff6ff",
                }}
              >
                {publicationAColumn2}
              </td>

              <td
                style={{
                  ...numberCellStyle,
                  backgroundColor: "#dbeafe",
                }}
              >
                {publicationATotal}
              </td>
            </tr>

            <tr>
              <td
                style={{
                  ...tableCellStyle,
                  fontWeight: "bold",
                  backgroundColor: "#f5f3ff",
                }}
              >
                B (Applied or Integrative)
              </td>

              <td
                style={{
                  ...numberCellStyle,
                  backgroundColor: "#f5f3ff",
                }}
              >
                {publicationBColumn1}
              </td>

              <td
                style={{
                  ...numberCellStyle,
                  backgroundColor: "#f5f3ff",
                }}
              >
                {publicationBColumn2}
              </td>

              <td
                style={{
                  ...numberCellStyle,
                  backgroundColor: "#ede9fe",
                }}
              >
                {publicationBTotal}
              </td>
            </tr>

            <tr>
              <td
                style={{
                  ...tableCellStyle,
                  fontWeight: "bold",
                  backgroundColor: "#fffbeb",
                }}
              >
                C (Teaching and Learning)
              </td>

              <td
                style={{
                  ...numberCellStyle,
                  backgroundColor: "#fffbeb",
                }}
              >
                {publicationCColumn1}
              </td>

              <td
                style={{
                  ...numberCellStyle,
                  backgroundColor: "#fffbeb",
                }}
              >
                {publicationCColumn2}
              </td>

              <td
                style={{
                  ...numberCellStyle,
                  backgroundColor: "#fef3c7",
                }}
              >
                {publicationCTotal}
              </td>
            </tr>
          </tbody>

          <tfoot>
            <tr>
              <td
                style={{
                  ...tableCellStyle,
                  fontWeight: "bold",
                  backgroundColor: "#e5e7eb",
                }}
              >
                Total
              </td>

              <td
                style={{
                  ...numberCellStyle,
                  backgroundColor: "#e5e7eb",
                }}
              >
                {publicationColumn1Total}
              </td>

              <td
                style={{
                  ...numberCellStyle,
                  backgroundColor: "#e5e7eb",
                }}
              >
                {publicationColumn2Total}
              </td>

              <td
                style={{
                  ...numberCellStyle,
                  backgroundColor: "#d1d5db",
                  fontSize: "1.1rem",
                }}
              >
                {publicationGrandTotal}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* ====================================================
          PUBLICATION TABLE — TABLE 2
      ==================================================== */}

      <div
        style={{
          ...sectionTitleStyle,
          marginTop: 32,
        }}
      >
        Publication Type / Publication Count — Table 2
      </div>

      <div
        style={{
          width: "100%",
          overflowX: "auto",
          marginTop: 12,
        }}
      >
        <table
          style={{
            width: "100%",
            minWidth: 1050,
            borderCollapse: "collapse",
            backgroundColor: "#fff",
          }}
        >
          <thead>
            <tr>
              <th
                rowSpan={2}
                style={{
                  ...tableHeaderStyle,
                  width: "20%",
                }}
              >
                Publication Type
              </th>

              <th colSpan={3} style={tableHeaderStyle}>
                Publication Count
              </th>

              <th
                rowSpan={2}
                style={{
                  ...tableHeaderStyle,
                  width: "12%",
                }}
              >
                Total
              </th>
            </tr>

            <tr>
              <th
                style={{
                  ...tableHeaderStyle,
                  width: "23%",
                }}
              >
                PRJ Articles (X) /
                <br />
                Books &amp; BCs (Y) /
                <br />
                Book Reviews (Z)
              </th>

              <th
                style={{
                  ...tableHeaderStyle,
                  width: "23%",
                }}
              >
                Trade Journals (X) /
                <br />
                Conf. Proceed. &amp; Present. (Y) /
                <br />
                Ind. Report, Textbooks &amp; Op-eds (Z)
              </th>

              <th
                style={{
                  ...tableHeaderStyle,
                  width: "22%",
                }}
              >
                Cases (X)
              </th>
            </tr>
          </thead>

          <tbody>
            {/* X — PRJ ARTICLES */}

            <tr>
              <td
                style={{
                  ...tableCellStyle,
                  fontWeight: "bold",
                  backgroundColor: "#eff6ff",
                }}
              >
                X (PRJ Articles)
              </td>

              <td
                style={{
                  ...numberCellStyle,
                  backgroundColor: "#eff6ff",
                }}
              >
                {table2XColumn1}
              </td>

              <td
                style={{
                  ...numberCellStyle,
                  backgroundColor: "#eff6ff",
                }}
              >
                {table2XColumn2}
              </td>

              <td
                style={{
                  ...numberCellStyle,
                  backgroundColor: "#eff6ff",
                }}
              >
                {table2XColumn3}
              </td>

              <td
                style={{
                  ...numberCellStyle,
                  backgroundColor: "#dbeafe",
                }}
              >
                {table2XTotal}
              </td>
            </tr>

            {/* Y — OTHER PEER-REVIEWED ICs */}

            <tr>
              <td
                style={{
                  ...tableCellStyle,
                  fontWeight: "bold",
                  backgroundColor: "#f5f3ff",
                }}
              >
                Y (Other Peer-reviewed ICs)
              </td>

              <td
                style={{
                  ...numberCellStyle,
                  backgroundColor: "#f5f3ff",
                }}
              >
                {table2YColumn1}
              </td>

              <td
                style={{
                  ...numberCellStyle,
                  backgroundColor: "#f5f3ff",
                }}
              >
                {table2YColumn2}
              </td>

              <td
                style={{
                  ...numberCellStyle,
                  backgroundColor: "#f5f3ff",
                }}
              >
                {table2YColumn3}
              </td>

              <td
                style={{
                  ...numberCellStyle,
                  backgroundColor: "#ede9fe",
                }}
              >
                {table2YTotal}
              </td>
            </tr>

            {/* Z — ALL OTHER ICs */}

            <tr>
              <td
                style={{
                  ...tableCellStyle,
                  fontWeight: "bold",
                  backgroundColor: "#fffbeb",
                }}
              >
                Z (All Other ICs)
              </td>

              <td
                style={{
                  ...numberCellStyle,
                  backgroundColor: "#fffbeb",
                }}
              >
                {table2ZColumn1}
              </td>

              <td
                style={{
                  ...numberCellStyle,
                  backgroundColor: "#fffbeb",
                }}
              >
                {table2ZColumn2}
              </td>

              <td
                style={{
                  ...numberCellStyle,
                  backgroundColor: "#fffbeb",
                }}
              >
                {table2ZColumn3}
              </td>

              <td
                style={{
                  ...numberCellStyle,
                  backgroundColor: "#fef3c7",
                }}
              >
                {table2ZTotal}
              </td>
            </tr>
          </tbody>

          {/* TABLE 2 BOTTOM TOTAL */}

          <tfoot>
            <tr>
              <td
                style={{
                  ...tableCellStyle,
                  fontWeight: "bold",
                  backgroundColor: "#e5e7eb",
                }}
              >
                Total
              </td>

              <td
                style={{
                  ...numberCellStyle,
                  backgroundColor: "#e5e7eb",
                }}
              >
                {table2Column1Total}
              </td>

              <td
                style={{
                  ...numberCellStyle,
                  backgroundColor: "#e5e7eb",
                }}
              >
                {table2Column2Total}
              </td>

              <td
                style={{
                  ...numberCellStyle,
                  backgroundColor: "#e5e7eb",
                }}
              >
                {table2Column3Total}
              </td>

              <td
                style={{
                  ...numberCellStyle,
                  backgroundColor: "#d1d5db",
                  fontSize: "1.1rem",
                }}
              >
                {table2GrandTotal}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* ====================================================
          MATCHED VARIABLES
      ==================================================== */}

      <p
        style={{
          color: "#555",
          marginTop: 24,
        }}
      >
        Matched &amp; scored variables: {scoredRows.length} of{" "}
        {Object.keys(flatMetrics).length} total.
      </p>

      {/* ====================================================
          FULL VARIABLE TABLE
      ==================================================== */}

      <table
        style={{
          width: "100%",
          borderCollapse: "collapse",
          marginTop: 16,
        }}
      >
        <thead>
          <tr>
            <th style={varHeaderStyle}>ID</th>

            <th style={varHeaderStyle}>Variable</th>

            <th style={varHeaderStyle}>Value</th>

            <th style={varHeaderStyle}>Point Weight</th>

            <th style={varHeaderStyle}>Score</th>
          </tr>
        </thead>

        <tbody>
          {scoredRows.map(({ key, value, id, weight, score }) => {
            let rowColor = "transparent";

            if (A111_KEYS.includes(key)) {
              rowColor = "#eff6ff";
            } else if (A112_TO_B_KEYS.includes(key)) {
              rowColor = "#f5f3ff";
            } else if (C_KEYS.includes(key)) {
              rowColor = "#fffbeb";
            }

            return (
              <tr
                key={key}
                style={{
                  backgroundColor: rowColor,
                }}
              >
                <td style={varCellStyle}>{id}</td>

                <td style={varCellStyle}>{key}</td>

                <td style={varCellStyle}>{String(value)}</td>

                <td style={varCellStyle}>{weight.toFixed(2)}</td>

                <td style={varCellStyle}>
                  {score !== null ? score.toFixed(2) : "N/A"}
                </td>
              </tr>
            );
          })}
        </tbody>

        <tfoot>
          <tr>
            <td
              colSpan={4}
              style={{
                ...varCellStyle,
                fontWeight: "bold",
              }}
            >
              Grand Total Score
            </td>

            <td
              style={{
                ...varCellStyle,
                fontWeight: "bold",
              }}
            >
              {grandTotal.toFixed(2)}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}
